import { z } from 'zod';

// Provider-specific configurations for STT
export const OpenAIListenConfigSchema = z.object({
  provider: z.literal('openai'),
  model: z.enum(['whisper-1', 'gpt-4o-transcribe']).default('gpt-4o-transcribe'),
  language: z.string().default('en'),
});
// Current-generation realtime transcription. gpt-live-transcribe is OpenAI's
// low-latency streaming model and the default; gpt-4o-transcribe is retained
// for backward compat but is marked legacy by OpenAI and — critically — has no
// vocabulary biasing, which is what mangles acronyms ("USC" -> "USA").
//
// NOTE: gpt-live-transcribe takes `languages: string[]`, NOT the singular
// `language`, and rejects a payload carrying both. The singular field is kept
// here as the stored shape; api/stt-session/route.ts maps it per-model.
export const OpenAIRealtimeListenConfigSchema = z.object({
  provider: z.literal('openai-realtime'), // Added provider literal for potential future union
  model: z.enum([
    'gpt-live-transcribe',
    'gpt-transcribe',
    // Legacy — retained for backward compat with un-migrated rows
    'whisper-1',
    'gpt-4o-transcribe',
  ]).default('gpt-live-transcribe'),
  language: z.string().default('en'),
  // Free-form context describing the room, and literal terms expected in the
  // audio (team names, acronyms, roster surnames). Supported by
  // gpt-live-transcribe / gpt-transcribe only; ignored for legacy models.
  prompt: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  // Latency/quality tradeoff for streaming transcription. Defaults to 'low'
  // in buildTranscriptionConfig; raise per-room only if word error rate
  // matters more than time-to-first-audio for that room.
  delay: z.enum(['minimal', 'low', 'medium', 'high', 'xhigh']).optional(),
});

// export const GoogleListenConfigSchema = z.object({
//   provider: z.literal('google'), // Added provider literal for potential future union
//   model: z.enum(['gemini-2.5-pro-exp-03-25', 'gemini-2.0-flash', 'gemini-2.0-flash-lite']),
//   language: z.string().default('en')
// });

// Define the schema for Groq STT provider config
export const GroqListenConfigSchema = z.object({
  provider: z.literal('groq'),
  model: z.enum(['whisper-large-v3']).default('whisper-large-v3'),
  language: z.string().default('en')
});

// xAI Grok STT. Server-side only: xAI authenticates with a Bearer header and a
// browser cannot set headers on a WebSocket, so audio is relayed through
// stt-api rather than going direct from the client (same constraint that
// XAISupportAgentProvider already works around for xAI Realtime).
//
// Parameters are query-string options on wss://api.x.ai/v1/stt, not a session
// payload. Deepgram-shaped: keyterm/endpointing/interim_results/Finalize.
export const XAIListenConfigSchema = z.object({
  provider: z.literal('xai'),
  // xAI does not publish model ids for STT; the endpoint selects the model.
  model: z.string().default('grok-stt'),
  language: z.string().default('en'),
  // Silence (ms) before the server declares speech_final. xAI's default of 10
  // is aggressive; 500 matches the endpointing our client VAD already applies.
  endpointing: z.number().int().min(0).max(5000).default(500),
  // Vocabulary biasing -- the direct analogue of OpenAI `keywords`.
  // API limit: max 100 terms, each <= 50 characters.
  keyterm: z.array(z.string()).max(100).optional(),
  interim_results: z.boolean().default(true),
  vad_threshold: z.number().min(0).max(1).optional(),
});

export const ListenProviderConfigSchema = z.discriminatedUnion('provider', [
  OpenAIListenConfigSchema,
  OpenAIRealtimeListenConfigSchema,
  // GoogleListenConfigSchema,
  GroqListenConfigSchema,
  XAIListenConfigSchema,
]);

// Provider-specific configurations for chat.
//
// Keystone D-014: the Zod enums are the server-side allow-list for chat models.
// Grok-3 series hard-blocked (retired by xAI Q1 2026); DB migration rewrites
// existing rows to the D-014 workload defaults. Legacy OpenAI models retained
// for backward compat on non-companion surfaces not yet Keystone-migrated.
export const OpenAIChatConfigSchema = z.object({
  provider: z.literal('openai'),
  model: z.enum([
    // D-014 defaults
    'gpt-5.4',
    'gpt-5.4-mini',
    'gpt-5.4-nano',
    // Legacy / previously supported — retained for backward compat
    'gpt-5',
    'gpt-4.1',
    'gpt-4.1-mini',
    'gpt-4.1-nano',
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-4-turbo',
    '03-mini',
  ]).default('gpt-5.4-nano'),
  language: z.string().default('en'),
  prompt: z.string().optional().default('You are a helpful AI assistant.'),
  temperature: z.number().optional().default(0.7),
  /**
   * #3683 — output cap for a conversational turn, forwarded as
   * `max_output_tokens`. The config route has written this into `language`
   * from `brainConfig.maxTokens` since #1663, but nothing typed it here and
   * nothing in signaling read it — the admin form's "Max Tokens" field was a
   * dead knob. Absent ⇒ signaling's default (three sentences of headroom).
   */
  maxTokens: z.number().int().positive().optional(),
});

export const XAIChatConfigSchema = z.object({
  provider: z.literal('xai'),
  // xAI retired grok-4-1-fast-* on 2026-05-15; the slugs still resolve but
  // route to slow grok-4.3 (>30s with full prompt → 10s SDK timeout). New
  // configurations should pick a grok-4.20-0309-* model; the retired slugs
  // are retained in the enum so legacy DB rows still validate during the
  // cutover (migration 0046 rewrites them to current-gen IDs).
  model: z.enum([
    'grok-4.20-0309-non-reasoning',
    'grok-4.20-0309-reasoning',
    'grok-4.20-multi-agent-0309',
    /** @deprecated retired by xAI 2026-05-15 — routes to slow grok-4.3 */
    'grok-4-1-fast-non-reasoning',
    /** @deprecated retired by xAI 2026-05-15 — routes to slow grok-4.3 */
    'grok-4-1-fast-reasoning',
  ]).default('grok-4.20-0309-non-reasoning'),
  language: z.string().default('en'),
  prompt: z.string().optional().default('You are a helpful AI assistant.'),
  temperature: z.number().optional().default(0.7),
  /**
   * #3683 — output cap for a conversational turn, forwarded as
   * `max_output_tokens`. The config route has written this into `language`
   * from `brainConfig.maxTokens` since #1663, but nothing typed it here and
   * nothing in signaling read it — the admin form's "Max Tokens" field was a
   * dead knob. Absent ⇒ signaling's default (three sentences of headroom).
   */
  maxTokens: z.number().int().positive().optional(),
});

// Google disabled — Keystone D-006: Gemini text migration out of scope, tracked
// for future Realtime work. Runtime (@telegamez/ai) does not instantiate a
// google client. Schema commented out rather than deleted so the branch is
// easy to restore when a GeminiResponsesAdapter ships. Do not re-add to the
// discriminated union below without shipping the adapter.
// export const GoogleChatConfigSchema = z.object({
//   provider: z.literal('google'),
//   model: z.enum(['gemini-2.5-pro-exp-03-25', 'gemini-2.0-flash', 'gemini-2.0-flash-lite']),
//   language: z.string().default('en'),
//   prompt: z.string(),
//   temperature: z.number().optional(),
// });

// Groq removed — never routed by Keystone D-014 and never shipped a production
// runtime path. If groq returns as a provider option in the future, restore
// from git history (pre-2026-04-23) rather than reviving dead branches here.

// Define the schema for language provider config as a discriminated union
export const ChatProviderConfigSchema = z.discriminatedUnion('provider', [
  OpenAIChatConfigSchema,
  XAIChatConfigSchema,
]);

// Create variants without the prompt field
export const OpenAIChatConfigNoPromptSchema = OpenAIChatConfigSchema.omit({ prompt: true });
export const XAIChatConfigNoPromptSchema = XAIChatConfigSchema.omit({ prompt: true });

// Define a schema variant where prompt is optional for providers that have it
export const ChatProviderConfigNoPromptSchema = z.discriminatedUnion('provider', [
  OpenAIChatConfigNoPromptSchema,
  XAIChatConfigNoPromptSchema,
  // Google disabled — Keystone D-006. Groq removed — never routed by D-014.
]);

// Provider-specific configurations for TTS
export const OpenAISpeechConfigSchema = z.object({
  provider: z.literal('openai'),
  model: z.enum(['tts-1', 'tts-1-hd']).default('tts-1'),
  voice: z.enum(['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer']).optional().default('alloy'),
  language: z.string().default('en'),
  speed: z.number().optional().default(1.0),
  response_format: z.enum(['mp3', 'opus', 'aac']).optional().default('mp3'),
});

export const DeepgramSpeechConfigSchema = z.object({
  provider: z.literal('deepgram'),
  model: z.enum(['nova-3', 'nova-2', 'nova-1', 'enhanced']).default('nova-2'),
  voice: z.enum(['asteria', 'luna', 'stella', 'athena', 'hera', 'orion', 'arcas', 'perseus', 'angus', 'orpheus', 'helios', 'zeus']).default('asteria'),
  encoding: z.enum(['mp3', 'opus']).optional().default('mp3'),
  language: z.string().default('en'),
});

// Define the schema for Cartesia speech provider config
export const CartesiaSpeechConfigSchema = z.object({
  provider: z.literal('cartesia'),
  model: z.enum(['sonic-2']).default('sonic-2'),
  voice: z.string().default('7ea5e9c2-b719-4dc3-b870-5ba5f14d31d8'), // Cartesia uses voice IDs (UUIDs)
  language: z.string().optional().default('en'),
  response_format: z.enum(['pcm16']).default('pcm16'),
  speed: z.number().optional().default(1),
  // Add other relevant Cartesia options if needed
});

// Define Inworld language codes (16 supported languages)
export const InworldLanguageCodeSchema = z.enum([
  'EN_US',  // English (US)
  'ZH_CN',  // Chinese (Simplified)
  'KO_KR',  // Korean
  'JA_JP',  // Japanese
  'RU_RU',  // Russian
  'IT_IT',  // Italian
  'ES_ES',  // Spanish
  'PT_BR',  // Portuguese (Brazil)
  'DE_DE',  // German
  'FR_FR',  // French
  'AR_SA',  // Arabic
  'PL_PL',  // Polish
  'NL_NL',  // Dutch
  'HI_IN',  // Hindi
  'HE_IL',  // Hebrew
  'AUTO',   // Auto-detect
]);

/**
 * The TTS-2 generation. `inworld-tts-2` is the default because it is the only model that
 * supports steering — Inworld's docs are explicit that the Flash variant "ignores
 * instruction tags entirely" — and steering is what carries the companion's emotion into
 * the voice. Flash stays selectable: it is 5x faster server-side and cheaper, which is the
 * right trade for anything that does not need to sound expressive.
 */
export const INWORLD_TTS_MODELS = ['inworld-tts-2', 'inworld-tts-2-flash'] as const;

/**
 * Retired model ids, mapped forward rather than rejected.
 *
 * TTS-1 and TTS-1-max were discontinued by Inworld on 2026-06-15; TTS-1.5 is deprecated.
 * The migration rewrites every row we know about, but a strict enum would turn any row it
 * missed — a restored backup, a preset written by an older build, a hand-edited config —
 * into a config that fails to parse rather than a room that speaks. Accepting the old id
 * and normalising it keeps a stale row degraded to "the new default" instead of broken.
 */
const RETIRED_INWORLD_TTS_MODELS = {
  'inworld-tts-1': 'inworld-tts-2',
  'inworld-tts-1-max': 'inworld-tts-2',
  'inworld-tts-1.5-mini': 'inworld-tts-2',
  'inworld-tts-1.5-max': 'inworld-tts-2',
} as const;

/**
 * How fast a companion speaks, as a multiplier on the voice's stock pace.
 *
 * Above 1.0 because the stock pace reads as slow in a live room. Measured against
 * `inworld-tts-2` on the streaming WebSocket rather than taken from the REST docs — that
 * surface is demonstrably trimmed (it carries no `instruction` field at all), so the field
 * being honoured there had to be shown: 1.3 rendered the same sentence 24.5% shorter and
 * 0.7 rendered it 41.7% longer, against a 6% run-to-run noise floor. Both are within a
 * percent of the exact reciprocal, so this is a true rate multiplier, not a hint.
 *
 * 1.1 is deliberately modest, and worth stating honestly: it measured 8% off the same
 * sentence, which is the same order as the 7% spread between untouched baseline renders.
 * On any single utterance that is not something a listener could pick out — but it is a
 * deterministic multiplier rather than run-to-run variance, so it holds across a whole
 * conversation where the variance averages out. If rooms still read as slow, this constant
 * is the one thing to move; a room wanting its own pace overrides it in its speech config.
 */
export const DEFAULT_INWORLD_SPEAKING_RATE = 1.1;

// Define the schema for Inworld speech provider config
export const InworldSpeechConfigSchema = z.object({
  provider: z.literal('inworld'),
  model: z
    .union([
      z.enum(INWORLD_TTS_MODELS),
      z
        .enum(
          Object.keys(RETIRED_INWORLD_TTS_MODELS) as [
            keyof typeof RETIRED_INWORLD_TTS_MODELS,
            ...(keyof typeof RETIRED_INWORLD_TTS_MODELS)[],
          ],
        )
        .transform((m) => RETIRED_INWORLD_TTS_MODELS[m]),
    ])
    .default('inworld-tts-2'),
  voice: z.string(), // Inworld uses workspace__voice format or voice UUID
  language: InworldLanguageCodeSchema.optional().default('EN_US'),
  /**
   * Rate multiplier on the voice's stock pace, sent as `audio_config.speaking_rate`.
   *
   * Bounded rather than free: the probe covered 0.7-1.3, and the band below is the
   * conservative envelope around it. A rate outside it is far more likely to be a typo
   * than an intention, and an unusable voice is worse than a slow one.
   */
  speakingRate: z.number().min(0.5).max(2).optional().default(DEFAULT_INWORLD_SPEAKING_RATE),
  /**
   * How a companion's delivery is directed. `inline` (the default when absent): the reply
   * model writes bracketed delivery notes where its delivery changes. `tag`: the opt-out — one
   * cue from the reply's EMOTION tag, held for the whole reply. Inline only applies on
   * `inworld-tts-2`; every other model gets the single cue. See
   * `_docs/Architecture/ConversationalAI/CompanionVoiceDirection-Plan.md`.
   */
  voiceDirection: z.enum(['tag', 'inline']).optional(),
});

// Define Inworld voice interface for API responses
export const InworldVoiceSchema = z.object({
  voiceId: z.string(),           // Format: workspace__voice or UUID
  name: z.string(),              // Internal name
  displayName: z.string(),       // Human-readable name
  description: z.string(),       // Voice characteristics description
  langCode: InworldLanguageCodeSchema,
  tags: z.array(z.string()),     // Voice attribute tags
});

// Shared Inworld voice ID validation - supports workspace__voice and UUID formats
export const INWORLD_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const INWORLD_WORKSPACE_VOICE_REGEX = /^[a-zA-Z0-9_-]+__[a-zA-Z0-9_-]+$/;

export function isValidInworldVoiceId(voiceId: string): boolean {
  return INWORLD_UUID_REGEX.test(voiceId) || INWORLD_WORKSPACE_VOICE_REGEX.test(voiceId);
}

// Define the schema for speech provider config as a discriminated union
export const SpeechProviderConfigSchema = z.discriminatedUnion('provider', [
  OpenAISpeechConfigSchema,
  DeepgramSpeechConfigSchema,
  CartesiaSpeechConfigSchema,
  InworldSpeechConfigSchema,
]);

// Define the schema for agent configuration
export const AgentConfigSchema = z.object({
  modelName: z.string(),
  provider: z.string(),
  systemPrompt: z.string().optional(),
  temperature: z.number().optional(),
  maxTokens: z.number().optional(),
  enabled: z.boolean().default(true),
  language: ChatProviderConfigSchema.optional().nullable(),
  speech_to_text: ListenProviderConfigSchema.optional().nullable(),
}).catchall(z.any());

// Define the schema for the entire config
export const ConfigSchema = z.object({
  agents: z.record(z.string(), AgentConfigSchema),
}).catchall(z.any());

/** Listening Configs */
export type OpenAIListenConfig = z.infer<typeof OpenAIListenConfigSchema>;
export type OpenAIRealtimeListenConfig = z.infer<typeof OpenAIRealtimeListenConfigSchema>;
// export type DeepgramListenConfig = z.infer<typeof DeepgramListenConfigSchema>;
// export type GoogleListenConfig = z.infer<typeof GoogleListenConfigSchema>;
export type GroqListenConfig = z.infer<typeof GroqListenConfigSchema>;
export type ListenProviderConfig = z.infer<typeof ListenProviderConfigSchema>;
export type XAIListenConfig = z.infer<typeof XAIListenConfigSchema>;

/** Chat Configs */
export type OpenAIChatConfig = z.infer<typeof OpenAIChatConfigSchema>;
export type XAIChatConfig = z.infer<typeof XAIChatConfigSchema>;
// GoogleChatConfig — re-export when GoogleChatConfigSchema is un-commented.
// GroqChatConfig — removed along with GroqChatConfigSchema.
export type ChatProviderConfig = z.infer<typeof ChatProviderConfigSchema>;
export type ChatProviderConfigNoPrompt = z.infer<typeof ChatProviderConfigNoPromptSchema>;

// Add types for the no-prompt variants
export type OpenAIChatConfigNoPrompt = z.infer<typeof OpenAIChatConfigNoPromptSchema>;
export type XAIChatConfigNoPrompt = z.infer<typeof XAIChatConfigNoPromptSchema>;

/** Speech Configs */
export type OpenAISpeechConfig = z.infer<typeof OpenAISpeechConfigSchema>;
export type DeepgramSpeechConfig = z.infer<typeof DeepgramSpeechConfigSchema>;
export type CartesiaSpeechConfig = z.infer<typeof CartesiaSpeechConfigSchema>;
export type InworldSpeechConfig = z.infer<typeof InworldSpeechConfigSchema>;
export type InworldLanguageCode = z.infer<typeof InworldLanguageCodeSchema>;
export type InworldVoice = z.infer<typeof InworldVoiceSchema>;
export type SpeechProviderConfig = z.infer<typeof SpeechProviderConfigSchema>;

/** Speech Provider Union Type */
export type SpeechProvider = 'openai' | 'deepgram' | 'cartesia' | 'inworld';

/** Agent Configs */
export type AgentConfig = z.infer<typeof AgentConfigSchema>;
export type Config = z.infer<typeof ConfigSchema>; 